import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';
import Layout from '../../components/common/pageLayout';
import Form from '../../components/Attendance/form';
import constant from '../../constant';
import { getLoggedInUserRoleId } from '../../utils';
import { hasPermission } from '../../utils/hasPermission';
import { v3_attendanceDetails, v3_attendanceUpdate, v3_projectMonthlyAttendance } from '../../actions/v3/attendance';

const errorMessage = (error, fallback) => typeof error === 'string' ? error : error?.message || fallback;

const Edit = ({ history }) => {
    const { state = {} } = useLocation();
    const { project, attendanceDate: requestedDate, month: requestedMonth } = state || {};
    const projectId = project?.PROJECT_ID;
    const canManageAttendance = Number(getLoggedInUserRoleId()) === 1 || hasPermission(constant.actionPermission.attendanceManageRecords);
    const [month, setMonth] = useState(requestedDate?.slice(0, 7) || requestedMonth || moment().format('YYYY-MM'));
    const [dates, setDates] = useState([]);
    const [loadedMonth, setLoadedMonth] = useState('');
    const [attendanceDate, setAttendanceDate] = useState('');
    const [data, setData] = useState(null);
    const [loadedDate, setLoadedDate] = useState('');
    const [isLoadingDates, setIsLoadingDates] = useState(false);
    const [isLoadingRecords, setIsLoadingRecords] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [datesError, setDatesError] = useState('');
    const [recordsError, setRecordsError] = useState('');

    useEffect(() => {
        let cancelled = false;
        setDates([]);
        setLoadedMonth('');
        setAttendanceDate('');
        setData(null);
        setDatesError('');
        setRecordsError('');
        if (!projectId || !month) {
            setIsLoadingDates(false);
            return;
        }
        setIsLoadingDates(true);
        const loadDates = async () => {
            try {
                const res = await v3_projectMonthlyAttendance(projectId, month.slice(5, 7), month.slice(0, 4));
                if (cancelled) return;
                if (!res.status) throw res.error;
                const savedDates = [...new Set((res.result.rows || []).map((row) => moment(row.DATE).format('YYYY-MM-DD')))].sort().reverse();
                setDates(savedDates);
                setLoadedMonth(month);
                const selectedDate = requestedDate?.startsWith(month) ? requestedDate : savedDates[0];
                if (selectedDate && !savedDates.includes(selectedDate)) {
                    setRecordsError('No saved attendance found for the selected date. Choose a saved attendance date.');
                } else {
                    setAttendanceDate(selectedDate || '');
                }
            } catch (error) {
                if (!cancelled) setDatesError(errorMessage(error, 'Unable to load saved attendance dates.'));
            } finally {
                if (!cancelled) setIsLoadingDates(false);
            }
        };
        loadDates();
        return () => { cancelled = true; };
    }, [projectId, month, requestedDate]);

    useEffect(() => {
        let cancelled = false;
        setData(null);
        setLoadedDate('');
        if (!attendanceDate || loadedMonth !== month) {
            setIsLoadingRecords(false);
            return;
        }
        setRecordsError('');
        setIsLoadingRecords(true);
        const loadRecords = async () => {
            try {
                const res = await v3_attendanceDetails(projectId, attendanceDate);
                if (cancelled) return;
                if (!res.status) throw res.error;
                setData(res.result.rows || []);
                setLoadedDate(attendanceDate);
            } catch (error) {
                if (!cancelled) setRecordsError(errorMessage(error, 'Unable to load attendance.'));
            } finally {
                if (!cancelled) setIsLoadingRecords(false);
            }
        };
        loadRecords();
        return () => { cancelled = true; };
    }, [attendanceDate, loadedMonth, month, projectId]);

    const hasRecords = data?.length > 0 && loadedDate === attendanceDate && loadedMonth === month;
    const onSubmit = async (rows) => {
        if (!canManageAttendance || !hasRecords || isSaving || isLoadingDates || isLoadingRecords) return;
        setIsSaving(true);
        try {
            const res = await v3_attendanceUpdate(projectId, attendanceDate, { rows });
            if (!res.status) throw res.error;
            toast.success(res.result?.message || 'Attendance updated successfully.');
            history.push(constant.Authedcomponent.attendanceMonthlyReport.url, { project, month });
        } catch (error) {
            toast.error(errorMessage(error, 'Unable to update attendance.'));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Layout title="Edit Attendance">
            <div className="card"><div className="card-body">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
                    <h4 className="mb-0">{project?.PROJECT_NAME} Project Time Sheet</h4>
                    <div className="d-flex gap-2">
                        <label>Month
                            <input type="month" aria-label="Attendance month" className="form-control" value={month}
                                disabled={isSaving || !projectId} onChange={(event) => setMonth(event.target.value)} />
                        </label>
                        <label>Saved attendance date
                            <select aria-label="Attendance date" className="form-control" value={attendanceDate}
                                disabled={isSaving || isLoadingDates || !dates.length || loadedMonth !== month}
                                onChange={(event) => setAttendanceDate(event.target.value)}>
                                <option value="">Select saved date</option>
                                {dates.map((date) => <option key={date} value={date}>{moment(date).format('DD-MMM-YYYY')}</option>)}
                            </select>
                        </label>
                    </div>
                </div>
                {!projectId && <p role="alert">Select a project from Manage Attendance to continue.</p>}
                {(datesError || recordsError) && <p role="alert">{datesError || recordsError}</p>}
                {(isLoadingDates || isLoadingRecords) && <p role="status">Loading saved attendance...</p>}
                {projectId && loadedMonth === month && !isLoadingDates && !isLoadingRecords && !recordsError &&
                    (!dates.length || (loadedDate === attendanceDate && !data?.length)) &&
                    <p>No saved attendance found. Select another month or date to edit an existing record.</p>}
                {hasRecords && !isLoadingDates && !isLoadingRecords && <Form data={data} onSubmit={onSubmit}
                    isEdit buttonName={isSaving ? 'Updating...' : 'Update attendance'} disabled={isSaving || !canManageAttendance} />}
                <Link className="btn btn-outline-secondary mt-3" to={{
                    pathname: projectId ? constant.Authedcomponent.attendanceMonthlyReport.url : constant.Authedcomponent.attendanceProjectList.url,
                    state: { project, month },
                }}>{projectId ? 'Back to monthly attendance' : 'Manage attendance'}</Link>
            </div></div>
        </Layout>
    );
};

export default Edit;
